import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import TenantModulesManagement from './TenantModulesManagement';
import PendingPaymentsManagement from './PendingPaymentsManagement';
import BulkImportUsers from './BulkImportUsers';
import TenantCustomPricingDialog from './TenantCustomPricingDialog';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { EmailSettingsCard } from './EmailSettingsCard';
import { SafeSelect as Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/SafeSelect';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } from '@/components/ui/pagination';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// Icons
import { 
  Building2,
  Users,
  GraduationCap,
  DollarSign,
  TrendingUp,
  UserPlus,
  Eye,
  Edit,
  Pause,
  Play,
  CreditCard,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Crown,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  UserCog,
  Bell,
  Key,
  Send,
  History,
  MessageCircle,
  Search,
  Filter,
  X
} from 'lucide-react';
import { LoadingButton } from '@/components/ui/loading-button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration Tab Component
const ConfigurationTab = () => {
  const [modulePricing, setModulePricing] = useState([]);
  const [orangeMoneyPhone, setOrangeMoneyPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newPrice, setNewPrice] = useState('');

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      const token = localStorage.getItem('token');
      const [modulesRes, orangeMoneyRes] = await Promise.all([
        axios.get(`${API}/super-admin/module-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/super-admin/orange-money-config`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setModulePricing(modulesRes.data);
      setOrangeMoneyPhone(orangeMoneyRes.data.phone_number || '');
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
      console.error('Error fetching configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (module) => {
    setEditingModule(module);
    setNewPrice(module.monthly_price.toString());
    setEditDialogOpen(true);
  };

  const handleUpdateModulePrice = async () => {
    const priceValue = parseFloat(newPrice);
    if (isNaN(priceValue) || priceValue < 0) {
      toast.error('Prix invalide');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/super-admin/module-pricing/${editingModule.module_id}`,
        { monthly_price: priceValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Prix mis à jour avec succès');
      setEditDialogOpen(false);
      fetchConfiguration();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du prix');
      console.error('Error updating price:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOrangeMoney = async () => {
    if (!orangeMoneyPhone.startsWith('+224')) {
      toast.error('Format invalide. Utilisez +224XXXXXXXXX');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/super-admin/orange-money-config`,
        { phone_number: orangeMoneyPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Numéro Orange Money mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      console.error('Error updating Orange Money:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-GN').format(price);
  };

  if (loading) {
    return <div className="p-6 text-center">Chargement...</div>;
  }

  // Séparer les modules de base (obligatoires payants) et autres modules payants
  const baseModules = modulePricing.filter(m => m.is_base_module && m.is_required);
  const paidModules = modulePricing.filter(m => !m.is_base_module);

  return (
    <div className="space-y-6">
      {/* Orange Money Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Phone className="w-5 h-5 mr-2" />
            Configuration Orange Money
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="orange_money_phone">Numéro Orange Money pour recevoir les paiements</Label>
            <div className="flex space-x-2 mt-2">
              <Input
                id="orange_money_phone"
                type="tel"
                placeholder="+224 6XX XX XX XX"
                value={orangeMoneyPhone}
                onChange={(e) => setOrangeMoneyPhone(e.target.value)}
              />
              <Button onClick={handleUpdateOrangeMoney} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Ce numéro recevra tous les paiements d'abonnement des établissements
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Module Pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Tarification des Modules
          </CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            Définissez le prix mensuel de chaque module. Les établissements paieront uniquement pour les modules activés.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pack de Base (Modules OBLIGATOIRES regroupés) */}
          {baseModules.length > 0 && (
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Pack de Base (Obligatoire)</h3>
              <p className="text-xs text-gray-600 mb-3">
                Ce pack regroupe tous les modules essentiels au fonctionnement du système. Il est obligatoire pour tous les établissements.
              </p>
              
              {/* Carte unique pour le Pack de Base */}
              <Card className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {/* En-tête du pack */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="flex -space-x-2">
                          {baseModules.slice(0, 3).map((module, idx) => (
                            <span key={idx} className="text-3xl bg-white rounded-full p-2 border-2 border-orange-200">
                              {module.icon}
                            </span>
                          ))}
                        </div>
                      </div>
                      <Badge className="bg-orange-600 text-white">
                        PACK OBLIGATOIRE
                      </Badge>
                    </div>

                    <div>
                      <h3 className="font-bold text-xl text-gray-800">Pack Modules de Base</h3>
                      <p className="text-sm text-gray-600 mt-1">Comprend {baseModules.length} modules essentiels</p>
                    </div>

                    {/* Liste des modules inclus */}
                    <div className="bg-white rounded-lg p-4 space-y-2">
                      <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Modules inclus :</p>
                      {baseModules.map((module) => (
                        <div key={module.module_id} className="flex items-center space-x-3 py-2 border-b border-gray-100 last:border-0">
                          <span className="text-xl">{module.icon}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800">{module.name}</p>
                            <p className="text-xs text-gray-500">{module.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-orange-600">
                              {formatPrice(module.monthly_price)} GNF
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Prix total du pack */}
                    <div className="bg-orange-600 text-white rounded-lg p-6 text-center">
                      <p className="text-sm uppercase tracking-wide mb-2">Prix total du pack</p>
                      <div className="text-4xl font-bold">
                        {formatPrice(baseModules.reduce((sum, m) => sum + m.monthly_price, 0))}
                        <span className="text-lg ml-2">GNF</span>
                      </div>
                      <p className="text-sm mt-2 opacity-90">par mois</p>
                    </div>

                    {/* Note informative */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        <span className="font-semibold">ℹ️ Note :</span> Ce pack est automatiquement inclus lors de la création d'un établissement. 
                        Le prix est calculé comme la somme des modules de base individuels.
                      </p>
                    </div>

                    {/* Bouton pour modifier les prix */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="w-full" variant="outline" size="lg">
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier les prix du pack
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Modifier les prix du Pack de Base</DialogTitle>
                          <DialogDescription>
                            Modifiez le prix mensuel de chaque module. Le prix total du pack sera recalculé automatiquement.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          {baseModules.map((module) => (
                            <Card key={module.module_id} className="border">
                              <CardContent className="pt-4">
                                <div className="flex items-center justify-between gap-4">
                                  <div className="flex items-center space-x-3 flex-1">
                                    <span className="text-2xl">{module.icon}</span>
                                    <div>
                                      <p className="font-semibold text-sm">{module.name}</p>
                                      <p className="text-xs text-gray-600">{module.description}</p>
                                    </div>
                                  </div>
                                  <Button
                                    onClick={() => handleOpenEditDialog(module)}
                                    variant="outline"
                                    size="sm"
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Modifier
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                          
                          <div className="bg-gray-50 rounded-lg p-4 mt-4">
                            <div className="flex justify-between items-center">
                              <p className="font-semibold text-gray-700">Prix total du pack :</p>
                              <p className="text-2xl font-bold text-orange-600">
                                {formatPrice(baseModules.reduce((sum, m) => sum + m.monthly_price, 0))} GNF
                              </p>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Modules Payants (Optionnels) */}
          <div>
            <h3 className="font-semibold text-sm text-gray-700 mb-3">Modules Complémentaires (Optionnels)</h3>
            <p className="text-xs text-gray-600 mb-3">
              Les établissements peuvent choisir d'activer ces modules selon leurs besoins.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paidModules.map((module) => (
                <Card key={module.module_id} className="border-2 hover:border-blue-300 transition-colors">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl">{module.icon}</span>
                        <Badge variant="outline" className="text-xs">
                          Payant
                        </Badge>
                      </div>
                      <h3 className="font-bold text-sm">{module.name}</h3>
                      <p className="text-xs text-gray-600 line-clamp-2">{module.description}</p>
                      <div className="text-center py-2">
                        <div className="text-2xl font-bold text-blue-600">
                          {formatPrice(module.monthly_price)}
                          <span className="text-sm text-gray-600"> GNF</span>
                        </div>
                        <p className="text-xs text-gray-500">par mois</p>
                      </div>
                      <Button
                        onClick={() => handleOpenEditDialog(module)}
                        variant="outline"
                        size="sm"
                        className="w-full"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier le prix
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Module Price Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le prix du module</DialogTitle>
            <DialogDescription>
              {editingModule && (
                <>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="text-2xl">{editingModule.icon}</span>
                    <span className="font-semibold">{editingModule.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{editingModule.description}</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="module_price">Prix mensuel (GNF)</Label>
              <Input
                id="module_price"
                type="number"
                placeholder="0"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="mt-2"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button onClick={handleUpdateModulePrice} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SuperAdminDashboard = ({ user }) => {
  const [platformStats, setPlatformStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modulePricing, setModulePricing] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Filtre et recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, suspended
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const downloadQuality = async (fmt) => {
    try {
      const res = await axios.get(`${API}/super-admin/quality-report`, { params: { fmt }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rapport_Qualite_EduScope.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Rapport qualité indisponible');
    }
  };
  const [selectedTenantForImport, setSelectedTenantForImport] = useState(null); // État séparé pour l'import
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  
  // États pour la gestion des admins
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [tenantAdmins, setTenantAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  
  // États pour les notifications
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: 'Rappel de Paiement d\'Abonnement',
    message: 'Votre abonnement nécessite un paiement. Veuillez vous connecter pour effectuer le paiement.'
  });
  const [notificationHistory, setNotificationHistory] = useState([]);
  
  // État pour stocker les modules du tenant sélectionné
  const [selectedTenantModules, setSelectedTenantModules] = useState([]);
  
  // État pour la configuration de tarification personnalisée
  const [customPricingDialogOpen, setCustomPricingDialogOpen] = useState(false);

  // Formulaire création établissement
  const [createForm, setCreateForm] = useState({
    name: '',
    contact_email: '',
    phone: '',
    address: '',
    domain: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_phone: '',
    initial_subscription_tier: 'STANDARD' // Valeur par défaut compatible avec le backend
  });

  // Calculer le prix du Pack de Base (somme des modules obligatoires)
  const basePackPrice = modulePricing
    .filter(m => m.is_base_module && m.is_required)
    .reduce((sum, m) => sum + m.monthly_price, 0);
  
  const tiers = {
    BASE_PACK: { 
      name: 'Pack de Base', 
      price: basePackPrice || 0, // Valeur par défaut si aucun module chargé
      description: 'Modules essentiels (Utilisateurs, Classes, Matières)'
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [statsResponse, tenantsResponse, paymentsResponse, modulesResponse] = await Promise.all([
        axios.get(`${API}/super-admin/platform-stats`),
        axios.get(`${API}/super-admin/tenants`),
        axios.get(`${API}/super-admin/payments`),
        axios.get(`${API}/super-admin/module-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setPlatformStats(statsResponse.data);
      setTenants(tenantsResponse.data);
      setPayments(paymentsResponse.data);
      setModulePricing(modulesResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    
    if (!createForm.name || !createForm.contact_email || !createForm.admin_email) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/super-admin/create-tenant-with-admin`, createForm);
      
      toast.success('Établissement créé avec succès !');
      setCreateDialogOpen(false);
      
      // Reset form
      setCreateForm({
        name: '',
        contact_email: '',
        phone: '',
        address: '',
        domain: '',
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        admin_phone: '',
        initial_subscription_tier: 'STANDARD' // Valeur par défaut compatible avec le backend
      });
      
      // Show admin credentials
      const credentials = response.data.admin_credentials;
      toast.info(`Admin créé: ${credentials.email} / ${credentials.temporary_password}`, {
        duration: 10000
      });
      
      await fetchDashboardData();
    } catch (error) {
      console.error('Error creating tenant:', error.response?.data);
      
      // Gestion des erreurs de validation Pydantic
      let errorMsg = 'Erreur lors de la création de l\'établissement';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si c'est une chaîne, l'utiliser directement
        if (typeof detail === 'string') {
          errorMsg = detail;
        } 
        // Si c'est un tableau d'erreurs de validation
        else if (Array.isArray(detail)) {
          errorMsg = detail.map(err => {
            const field = err.loc?.join('.') || 'champ';
            return `${field}: ${err.msg}`;
          }).join(', ');
        }
        // Si c'est un objet
        else if (typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async (action, tier = null) => {
    if (!selectedTenant) return;

    try {
      setLoading(true);
      
      // Gérer suspension/activation avec les nouveaux endpoints
      if (action === 'suspend') {
        await axios.put(`${API}/super-admin/tenants/${selectedTenant.tenant.id}/suspend`);
        toast.success('Établissement suspendu avec succès');
      } else if (action === 'reactivate') {
        await axios.put(`${API}/super-admin/tenants/${selectedTenant.tenant.id}/activate`);
        toast.success('Établissement réactivé avec succès');
      } else {
        // Autres actions (changement de plan, etc.)
        const data = { action };
        if (tier) data.tier = tier;
        await axios.put(`${API}/super-admin/tenant/${selectedTenant.tenant.id}/subscription`, data);
        toast.success(`Action "${action}" effectuée avec succès`);
      }
      
      setManageDialogOpen(false);
      await fetchDashboardData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la gestion';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Gestion des administrateurs d'établissements
  const fetchTenantAdmins = async (tenantId) => {
    try {
      setLoadingAdmins(true);
      const response = await axios.get(`${API}/super-admin/tenant/${tenantId}/admins`);
      setTenantAdmins(response.data);
    } catch (error) {
      console.error('Error fetching tenant admins:', error);
      toast.error('Erreur lors du chargement des administrateurs');
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Charger les modules d'un tenant
  const fetchTenantModules = async (tenantId) => {
    try {
      const [modulesResponse, pricingResponse] = await Promise.all([
        axios.get(`${API}/super-admin/tenants/${tenantId}/modules`),
        axios.get(`${API}/super-admin/module-pricing`) // Endpoint Super Admin correct
      ]);
      
      const enabledModuleIds = modulesResponse.data?.enabled_modules || [];
      const allModules = pricingResponse.data || [];
      
      // Enrichir avec les infos de pricing
      const enrichedModules = enabledModuleIds.map(moduleId => {
        const moduleInfo = allModules.find(m => m.module_id === moduleId);
        return moduleInfo || { module_id: moduleId, name: moduleId, monthly_price: 0 };
      });
      
      setSelectedTenantModules(enrichedModules);
    } catch (error) {
      console.error('Error fetching tenant modules:', error);
      toast.error('Erreur lors du chargement des modules');
      setSelectedTenantModules([]);
    }
  };

  // Annuler un module
  const handleCancelModule = async (moduleId, moduleName) => {
    if (!selectedTenant) return;
    
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir annuler le module "${moduleName}" pour cet établissement ?\n\nCette action retirera l'accès au module.`
    );
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/super-admin/tenants/${selectedTenant.tenant.id}/modules/${moduleId}`);
      
      toast.success(`Module "${moduleName}" annulé avec succès`);
      
      // Recharger les modules
      await fetchTenantModules(selectedTenant.tenant.id);
      await fetchDashboardData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'annulation du module';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetAdminPassword = async (adminId) => {
    if (!selectedTenant) return;

    try {
      setLoading(true);
      const response = await axios.post(`${API}/super-admin/tenant/${selectedTenant.tenant.id}/reset-admin-password/${adminId}`);
      
      toast.success('Mot de passe réinitialisé avec succès !');
      toast.info(`Nouveau mot de passe: ${response.data.temporary_password}`, {
        duration: 10000
      });
      
      await fetchTenantAdmins(selectedTenant.tenant.id);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la réinitialisation';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Gestion des notifications
  const handleSendNotification = async () => {
    if (!selectedTenant) return;

    try {
      setLoading(true);
      const response = await axios.post(`${API}/super-admin/notify-payment/${selectedTenant.tenant.id}`, notificationForm);
      
      toast.success(response.data.message);
      setNotifyDialogOpen(false);
      
      // Reset form
      setNotificationForm({
        title: 'Rappel de Paiement d\'Abonnement',
        message: 'Votre abonnement nécessite un paiement. Veuillez vous connecter pour effectuer le paiement.'
      });
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi de la notification';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationHistory = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/notifications/history`);
      setNotificationHistory(response.data);
    } catch (error) {
      console.error('Error fetching notification history:', error);
      toast.error('Erreur lors du chargement de l\'historique des notifications');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (isActive) => {
    // Nouveau système basé sur tenant.is_active
    if (isActive === false) {
      return 'Suspendu';
    }
    return 'Actif';
  };

  const getStatusColor = (isActive) => {
    // Couleurs basées sur tenant.is_active
    if (isActive === false) {
      return 'bg-red-500'; // Suspendu
    }
    return 'bg-green-500'; // Actif
  };

  if (loading && !platformStats) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Chargement du dashboard Super Admin...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Super Admin</h1>
          <p className="text-gray-600 mt-1">
            Gestion globale de la plateforme EduScope SaaS
          </p>
        </div>
        
        <div className="flex flex-wrap items-center justify-end gap-3 whitespace-nowrap">
          <button type="button" onClick={() => downloadQuality('pdf')} data-testid="download-quality-report-pdf-btn"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            Rapport qualité (PDF)
          </button>
          <button type="button" onClick={() => downloadQuality('md')} data-testid="download-quality-report-md-btn"
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50">
            Texte
          </button>
          {/* Bouton Manuel d'Utilisation */}
          <a 
            href="/MANUEL_UTILISATEUR_EDUSCOPE.pdf" 
            download="Manuel_EduScope.pdf"
            data-testid="download-manuel-btn"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Manuel Utilisateur
          </a>

          {/* Bouton Proposition Institutionnelle - Simandou 2040 */}
          <a 
            href="/PROPOSITION_ETAT_SIMANDOU2040_EDUSCOPE.pdf" 
            download="Proposition_EduScope_Simandou2040.pdf"
            data-testid="download-proposition-simandou-btn"
            className="inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-600 to-teal-500 hover:from-cyan-700 hover:to-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Dossier Simandou 2040
          </a>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Building2 className="w-4 h-4 mr-2" />
                Nouvel Établissement
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un Nouvel Établissement</DialogTitle>
              <DialogDescription>
                Créer un établissement avec son administrateur et abonnement d'essai
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateTenant} className="space-y-6">
              {/* Informations établissement */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Informations de l'Établissement</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom de l'établissement *</Label>
                    <Input
                      id="name"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                      placeholder="Ex: Lycée Bilingue de Douala"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email de contact *</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={createForm.contact_email}
                      onChange={(e) => setCreateForm({...createForm, contact_email: e.target.value})}
                      placeholder="contact@lycee-douala.edu"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
                      placeholder="+224 6XX XX XX XX"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="domain">Domaine (optionnel)</Label>
                    <Input
                      id="domain"
                      value={createForm.domain}
                      onChange={(e) => setCreateForm({...createForm, domain: e.target.value})}
                      placeholder="lycee-douala.eduscope.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Textarea
                    id="address"
                    value={createForm.address}
                    onChange={(e) => setCreateForm({...createForm, address: e.target.value})}
                    placeholder="Adresse complète de l'établissement"
                    rows={2}
                  />
                </div>
              </div>

              {/* Informations administrateur */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Administrateur Principal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin_first_name">Prénom *</Label>
                    <Input
                      id="admin_first_name"
                      value={createForm.admin_first_name}
                      onChange={(e) => setCreateForm({...createForm, admin_first_name: e.target.value})}
                      placeholder="Jean"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="admin_last_name">Nom *</Label>
                    <Input
                      id="admin_last_name"
                      value={createForm.admin_last_name}
                      onChange={(e) => setCreateForm({...createForm, admin_last_name: e.target.value})}
                      placeholder="Dupont"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="admin_email">Email admin *</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      value={createForm.admin_email}
                      onChange={(e) => setCreateForm({...createForm, admin_email: e.target.value})}
                      placeholder="directeur@lycee-douala.edu"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="admin_phone">Téléphone admin</Label>
                    <Input
                      id="admin_phone"
                      value={createForm.admin_phone}
                      onChange={(e) => setCreateForm({...createForm, admin_phone: e.target.value})}
                      placeholder="+224 6XX XX XX XX"
                    />
                  </div>
                </div>
              </div>

              {/* Pack de Base obligatoire */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Pack d'Abonnement Initial</h3>
                
                {/* Affichage du Pack de Base (seule option) */}
                <div className="border-2 border-orange-300 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">📦</span>
                      <span className="font-semibold text-lg">Pack de Base</span>
                    </div>
                    <Badge className="bg-orange-600 text-white">OBLIGATOIRE</Badge>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    Modules essentiels : Utilisateurs, Classes, Matières
                  </p>
                  
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-sm text-gray-600">Prix mensuel</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatPrice(basePackPrice)} <span className="text-base text-gray-600">GNF</span>
                    </p>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <span className="font-semibold">🎁 Période d'essai :</span> L'établissement bénéficiera d'un essai gratuit de 30 jours avec accès complet aux modules de base.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateDialogOpen(false)}
                >
                  Annuler
                </Button>
                <LoadingButton 
                  type="submit" 
                  loading={loading}
                  loadingText="Création de l'établissement..."
                >
                  Créer l'Établissement
                </LoadingButton>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Statistiques globales */}
      {platformStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Établissements</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{platformStats.total_tenants}</div>
              <p className="text-xs text-muted-foreground">
                {platformStats.active_subscriptions} avec modules de base, {platformStats.trial_subscriptions} paiements en attente
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Utilisateurs</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {platformStats.total_students + platformStats.total_teachers + (platformStats.total_admins || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                {platformStats.total_students} étudiants, {platformStats.total_teachers} enseignants, {platformStats.total_admins || 0} admins
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Revenus Mensuel</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatPrice(platformStats.monthly_revenue)} GNF
              </div>
              <p className="text-xs text-muted-foreground">
                Annuel: {formatPrice(platformStats.annual_revenue)} GNF
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Croissance</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">+{platformStats.growth_this_month || 0}</div>
              <p className="text-xs text-muted-foreground">
                Nouveaux établissements ce mois
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contenu principal avec onglets */}
      <Tabs defaultValue="tenants" className="w-full">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="tenants">Établissements</TabsTrigger>
          <TabsTrigger value="modules">Modules</TabsTrigger>
          <TabsTrigger value="payments">Paiements</TabsTrigger>
          <TabsTrigger value="import">Import Utilisateurs</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="admins">Administrateurs</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
        </TabsList>
        
        {/* Onglet Établissements */}
        <TabsContent value="tenants" className="space-y-4">
          {/* Barre de recherche et filtre */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Rechercher un établissement..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset à la page 1 lors de la recherche
                }}
                className="pl-10"
              />
            </div>
            
            <div className="w-full sm:w-64">
              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1); // Reset à la page 1 lors du changement de filtre
              }}>
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <SelectValue placeholder="Filtrer par statut" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="suspended">Suspendus</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid gap-4">
            {(() => {
              // Filtrage et recherche
              let filteredTenants = tenants.filter(tenant => {
                // Filtre par recherche
                const matchesSearch = searchTerm === '' || 
                  tenant.tenant?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  tenant.tenant?.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
                
                // Filtre par statut (basé sur tenant.is_active)
                const matchesStatus = statusFilter === 'all' ||
                  (statusFilter === 'active' && tenant.tenant?.is_active !== false) ||
                  (statusFilter === 'suspended' && tenant.tenant?.is_active === false);
                
                return matchesSearch && matchesStatus;
              });
              
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedTenants = filteredTenants.slice(startIndex, endIndex);
              const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);
              
              return (
                <>
                  {paginatedTenants.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun établissement trouvé</h3>
                        <p className="text-gray-600">
                          {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Aucun établissement avec ce statut'}
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    paginatedTenants.map((tenant, index) => (
              <Card key={`${tenant.tenant?.id}-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold">{tenant.tenant?.name}</h3>
                        <Badge className={`${getStatusColor(tenant.tenant?.is_active)} text-white`}>
                          {getStatusText(tenant.tenant?.is_active)}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {tenant.tenant?.contact_email}
                        </div>
                        {tenant.tenant?.phone && (
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-1" />
                            {tenant.tenant?.phone}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Créé le {formatDate(tenant.tenant?.created_at)}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6 mt-2">
                        <div className="text-sm">
                          <span className="font-medium">Étudiants:</span> {tenant.stats?.student_count || 0}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Enseignants:</span> {tenant.stats?.teacher_count || 0}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Revenus:</span> {formatPrice(tenant.revenue?.total_paid || 0)} GNF
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Admins:</span> {tenant.admin_users?.length || 0}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          fetchTenantModules(tenant.tenant.id);
                          setManageDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Gérer
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          fetchTenantAdmins(tenant.tenant.id);
                          setAdminDialogOpen(true);
                        }}
                      >
                        <UserCog className="w-4 h-4 mr-2" />
                        Admins
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTenant(tenant);
                          setNotifyDialogOpen(true);
                        }}
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Notifier
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTenant(tenant.tenant);
                          setCustomPricingDialogOpen(true);
                        }}
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        Tarifs
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
                    ))
                  )}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                          
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <PaginationItem key={page}>
                              <PaginationLink
                                onClick={() => setCurrentPage(page)}
                                isActive={currentPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                          
                          <PaginationItem>
                            <PaginationNext 
                              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                              className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* Onglet Modules */}
        <TabsContent value="modules" className="space-y-4">
          <TenantModulesManagement user={user} />
        </TabsContent>

        {/* Onglet Paiements */}
        <TabsContent value="payments" className="space-y-4">
          {/* Pending Payments Management */}
          <PendingPaymentsManagement />

          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle>Historique des Paiements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {payments.slice(0, 20).map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="font-medium">{payment.tenant_name}</div>
                      <div className="text-sm text-gray-600">
                        {payment.description} • {formatDate(payment.created_at)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Transaction: {payment.transaction_id}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">
                        +{formatPrice(payment.amount)} GNF
                      </div>
                      <div className="text-xs text-gray-500">
                        {payment.payment_method}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Import Utilisateurs */}
        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sélectionner un Établissement</CardTitle>
              <CardDescription>
                Choisissez l'établissement pour lequel vous souhaitez importer des utilisateurs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select 
                value={selectedTenantForImport?.id || ''} 
                onValueChange={(value) => {
                  const tenant = tenants.find(t => t.tenant?.id === value);
                  setSelectedTenantForImport(tenant?.tenant || null);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choisir un établissement..." />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.tenant?.id} value={t.tenant?.id}>
                      {t.tenant?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {selectedTenantForImport && (
            <BulkImportUsers 
              tenantId={selectedTenantForImport.id}
              tenantName={selectedTenantForImport.name}
              onImportComplete={(summary) => {
                toast.success(`Import terminé pour ${selectedTenantForImport.name}!`);
                fetchPlatformStats(); // Rafraîchir les stats
              }}
            />
          )}
        </TabsContent>

        {/* Onglet Analytics */}
        <TabsContent value="analytics" className="space-y-4">
          {platformStats && (
            <>
              {/* Top tenants payants */}
              {platformStats.top_paying_tenants?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Meilleurs Clients</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {platformStats.top_paying_tenants.map((tenant, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div>
                            <div>
                              <div className="font-medium">{tenant.tenant_name}</div>
                              <div className="text-sm text-gray-600">
                                {tenant.payment_count} paiement{tenant.payment_count > 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                          <div className="font-bold text-green-600">
                            {formatPrice(tenant.total_paid)} GNF
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Inscriptions récentes */}
              {platformStats.recent_signups?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Inscriptions Récentes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {platformStats.recent_signups.map((signup, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{signup.name}</div>
                            <div className="text-sm text-gray-600">{signup.contact_email}</div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(signup.created_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Onglet Administrateurs */}
        <TabsContent value="admins" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <UserCog className="w-5 h-5 mr-2" />
                Gestion des Administrateurs d'Établissements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tenants.map((tenant) => (
                  <div key={tenant.tenant?.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{tenant.tenant?.name}</h3>
                        <p className="text-sm text-gray-600">{tenant.tenant?.contact_email}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Badge>{tenant.admin_users?.length || 0} admin(s)</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedTenant(tenant);
                            fetchTenantAdmins(tenant.tenant.id);
                            setAdminDialogOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Voir les admins
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Notifications */}
        <TabsContent value="notifications" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Système de Notifications</h2>
            <Button onClick={fetchNotificationHistory}>
              <History className="w-4 h-4 mr-2" />
              Actualiser l'historique
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Envoi rapide de notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Send className="w-5 h-5 mr-2" />
                  Envoi Rapide de Rappels
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {tenants.map((tenant) => (
                  <div key={tenant.tenant?.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <div className="font-medium">{tenant.tenant?.name}</div>
                      <div className="text-xs text-gray-500">
                        {tenant.admin_users?.length || 0} admin(s) • 
                        Status: {getStatusText(tenant.subscription?.status)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setNotifyDialogOpen(true);
                      }}
                    >
                      <Bell className="w-4 h-4 mr-1" />
                      Notifier
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Historique des notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <History className="w-5 h-5 mr-2" />
                  Historique des Notifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {notificationHistory.length > 0 ? (
                    notificationHistory.map((notif, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded">
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{notif.tenant_name}</div>
                          <div className="text-xs text-gray-500">
                            {formatDate(notif.created_at)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {notif.title}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Aucune notification envoyée pour le moment
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Onglet Configuration */}
        <TabsContent value="configuration" className="space-y-4">
          <ConfigurationTab />
        </TabsContent>
      </Tabs>

      {/* Dialog de gestion d'établissement */}
      <Dialog open={manageDialogOpen} onOpenChange={setManageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gérer l'Établissement</DialogTitle>
            <DialogDescription>
              {selectedTenant?.tenant?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTenant && (
            <div className="space-y-6">
              <EmailSettingsCard tenantId={selectedTenant.tenant?.id} />
              {/* Informations actuelles */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Statut:</span> {getStatusText(selectedTenant.tenant?.is_active)}
                  </div>
                  <div>
                    <span className="font-medium">Étudiants:</span> {selectedTenant.stats?.student_count}/{selectedTenant.subscription?.max_students}
                  </div>
                  <div>
                    <span className="font-medium">Revenus total:</span> {formatPrice(selectedTenant.revenue?.total_paid || 0)} GNF
                  </div>
                  {/* Date selon le statut */}
                  {selectedTenant.subscription?.status === 'ACTIVE' && selectedTenant.subscription?.end_date && (
                    <div className="col-span-2">
                      <span className="font-medium">Date de fin d'abonnement:</span> {formatDate(selectedTenant.subscription.end_date)}
                    </div>
                  )}
                  {selectedTenant.subscription?.status === 'SUSPENDED' && selectedTenant.subscription?.updated_at && (
                    <div className="col-span-2">
                      <span className="font-medium">Suspendu depuis le:</span> {formatDate(selectedTenant.subscription.updated_at)}
                    </div>
                  )}
                  {selectedTenant.subscription?.status === 'TRIAL' && selectedTenant.subscription?.end_date && (
                    <div className="col-span-2">
                      <span className="font-medium">Date de fin d'essai:</span> {formatDate(selectedTenant.subscription.end_date)}
                    </div>
                  )}
                  {selectedTenant.subscription?.status === 'NO_SUBSCRIPTION' && selectedTenant.subscription?.created_at && (
                    <div className="col-span-2">
                      <span className="font-medium">Inconnu depuis le:</span> {formatDate(selectedTenant.subscription.created_at)}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions rapides */}
              <div className="space-y-3">
                <h4 className="font-medium">Actions Rapides</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  {selectedTenant.tenant?.is_active !== false ? (
                    <Button
                      variant="outline"
                      onClick={() => handleManageSubscription('suspend')}
                      disabled={loading}
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    >
                      <Pause className="w-4 h-4 mr-2" />
                      Suspendre l'Établissement
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => handleManageSubscription('reactivate')}
                      disabled={loading}
                      className="text-green-600 border-green-200 hover:bg-green-50"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Réactiver l'Établissement
                    </Button>
                  )}
                </div>

                {/* Modules Souscrits */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Modules Souscrits</label>
                  {selectedTenantModules.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                      {selectedTenantModules.map((module) => (
                        <div 
                          key={module.module_id} 
                          className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium">{module.name}</p>
                            <p className="text-xs text-gray-500">
                              {module.monthly_price?.toLocaleString()} GNF/mois
                            </p>
                          </div>
                          {!module.is_base_module && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelModule(module.module_id, module.name)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Annuler
                            </Button>
                          )}
                        </div>
                      ))}
                      <div className="pt-2 border-t mt-2">
                        <p className="text-sm font-medium">
                          Total mensuel: {selectedTenantModules.reduce((sum, m) => sum + (m.monthly_price || 0), 0).toLocaleString()} GNF
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded border text-center">
                      Aucun module souscrit
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de gestion des administrateurs */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <UserCog className="w-5 h-5 mr-2" />
              Administrateurs - {selectedTenant?.tenant?.name}
            </DialogTitle>
            <DialogDescription>
              Gestion des comptes administrateurs de l'établissement
            </DialogDescription>
          </DialogHeader>
          
          {loadingAdmins ? (
            <div className="flex justify-center p-4">
              <div className="text-gray-600">Chargement des administrateurs...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {tenantAdmins.length > 0 ? (
                tenantAdmins.map((admin) => (
                  <div key={admin.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="font-medium">
                          {admin.first_name} {admin.last_name}
                        </div>
                        <div className="text-sm text-gray-600">{admin.email}</div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={admin.role === 'ADMIN' ? 'default' : 'secondary'}>
                            {admin.role}
                          </Badge>
                          <Badge variant={admin.status === 'ACTIVE' ? 'default' : 'destructive'}>
                            {admin.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500">
                          Créé le {formatDate(admin.created_at)}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <LoadingButton
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetAdminPassword(admin.id)}
                          loading={loading}
                          loadingText="Réinitialisation..."
                        >
                          <Key className="w-4 h-4 mr-2" />
                          Réinitialiser mot de passe
                        </LoadingButton>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Aucun administrateur trouvé pour cet établissement
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de notification */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <MessageCircle className="w-5 h-5 mr-2" />
              Envoyer une Notification
            </DialogTitle>
            <DialogDescription>
              Envoyer un rappel de paiement à {selectedTenant?.tenant?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notif-title">Titre du message</Label>
              <Input
                id="notif-title"
                value={notificationForm.title}
                onChange={(e) => setNotificationForm({
                  ...notificationForm, 
                  title: e.target.value
                })}
                placeholder="Titre de la notification"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notif-message">Message</Label>
              <Textarea
                id="notif-message"
                value={notificationForm.message}
                onChange={(e) => setNotificationForm({
                  ...notificationForm, 
                  message: e.target.value
                })}
                placeholder="Contenu du message de rappel"
                rows={4}
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-800">
                <strong>Destinataires:</strong> {selectedTenant?.admin_users?.length || 0} administrateur(s)
              </div>
              <div className="text-xs text-blue-600 mt-1">
                La notification sera envoyée à tous les administrateurs de l'établissement
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setNotifyDialogOpen(false)}
            >
              Annuler
            </Button>
            <LoadingButton 
              onClick={handleSendNotification}
              disabled={!notificationForm.title || !notificationForm.message}
              loading={loading}
              loadingText="Envoi de la notification..."
            >
              Envoyer la notification
            </LoadingButton>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog de configuration de tarification personnalisée */}
      <TenantCustomPricingDialog
        open={customPricingDialogOpen}
        onOpenChange={setCustomPricingDialogOpen}
        tenant={selectedTenant}
      />
    </div>
  );
};

export default SuperAdminDashboard;